/**
 * usePixelHistory.js
 * Undo/Redo-Hook fuer den Pixel-Editor.
 * Speichert ImageData-Snapshots (maximal MAX_HISTORY Eintraege).
 */

import { useState, useCallback, useRef } from 'react';

const MAX_HISTORY = 50;

export function usePixelHistory(initialData) {
  const historyRef = useRef([]);
  const positionRef = useRef(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const updateState = () => {
    setCanUndo(positionRef.current > 0);
    setCanRedo(positionRef.current < historyRef.current.length - 1);
  };

  const push = useCallback((imageData) => {
    if (!imageData) return;
    // Alles nach der aktuellen Position loeschen (neue Aenderung macht Redo unmoеglich)
    historyRef.current = historyRef.current.slice(0, positionRef.current + 1);

    // Kopie erstellen
    const copy = new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
    historyRef.current.push(copy);

    // History begrenzen
    if (historyRef.current.length > MAX_HISTORY) {
      historyRef.current.shift();
    }
    positionRef.current = historyRef.current.length - 1;
    updateState();
  }, []);

  const undo = useCallback(() => {
    if (positionRef.current <= 0) return null;
    positionRef.current -= 1;
    const snapshot = historyRef.current[positionRef.current];
    updateState();
    return new ImageData(new Uint8ClampedArray(snapshot.data), snapshot.width, snapshot.height);
  }, []);

  const redo = useCallback(() => {
    if (positionRef.current >= historyRef.current.length - 1) return null;
    positionRef.current += 1;
    const snapshot = historyRef.current[positionRef.current];
    updateState();
    return new ImageData(new Uint8ClampedArray(snapshot.data), snapshot.width, snapshot.height);
  }, []);

  const reset = useCallback(() => {
    historyRef.current = [];
    positionRef.current = -1;
    setCanUndo(false);
    setCanRedo(false);
  }, []);

  return { push, undo, redo, reset, canUndo, canRedo };
}
